import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Home, Search, LayoutGrid, ArrowRight, Package } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Category {
  id: number;
  name: string;
  image_url?: string;
  subCategories?: Category[];
}

const AllCategoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [fullTree, setFullTree] = useState<Category[]>([]);
  const [currentList, setCurrentList] = useState<Category[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<Category[]>([]); 
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('/nodejs/api/categories');
        const data = await response.json();
        setFullTree(data);
        setCurrentList(data);
      } catch (err) { console.error(err); } 
      finally { setLoading(false); }
    };
    fetchCategories();
  }, []);

  const handleCategoryClick = (category: Category) => {
    if (category.subCategories && category.subCategories.length > 0) {
      setBreadcrumbs([...breadcrumbs, category]);
      setCurrentList(category.subCategories);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate(`/categories/${category.id}`);
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      setBreadcrumbs([]);
      setCurrentList(fullTree);
    } else {
      const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
      setBreadcrumbs(newBreadcrumbs);
      setCurrentList(newBreadcrumbs[newBreadcrumbs.length - 1].subCategories || []);
    }
  };

  const filteredList = currentList.filter(cat => 
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <article className="min-h-screen bg-[#f4f7f6] pb-20 font-sans antialiased">
      {/* --- COMPACT SEARCH BAR --- */}
      <section className="bg-white border-b border-gray-200 py-4 px-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <LayoutGrid className="text-[#00684a]" size={20} />
            <h1 className="text-lg font-bold text-gray-800">Sourcing Directory</h1>
          </div>
          
          <div className="relative w-full max-w-md group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input 
              type="text"
              placeholder="Search sub-categories..."
              className="w-full pl-10 pr-4 py-2 rounded-lg bg-gray-100 border-transparent focus:bg-white focus:ring-2 focus:ring-[#00684a]/20 focus:border-[#00684a] outline-none transition-all text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto mt-4 px-4 sm:px-6 lg:px-8">
        
        {/* --- HIERARCHY BREADCRUMBS --- */}
        <nav className="mb-6 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-gray-500 overflow-x-auto no-scrollbar bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
          <button onClick={() => handleBreadcrumbClick(-1)} className="hover:text-[#00684a] flex items-center gap-1">
            <Home size={14} /> MAIN
          </button>
          
          {breadcrumbs.map((crumb, idx) => (
            <React.Fragment key={crumb.id}>
              <ChevronRight size={12} className="text-gray-300 mx-1" />
              <button 
                onClick={() => handleBreadcrumbClick(idx)}
                className={idx === breadcrumbs.length - 1 ? 'text-[#00684a]' : 'hover:text-gray-800'}
              >
                {crumb.name}
              </button>
            </React.Fragment>
          ))}
        </nav>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
             {[...Array(6)].map((_, i) => <div key={i} className="h-40 bg-gray-200 animate-pulse rounded-xl" />)}
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div 
              key={breadcrumbs.length}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {filteredList.map((cat) => (
                <motion.div
                  key={cat.id}
                  whileHover={{ borderColor: '#00684a' }}
                  onClick={() => handleCategoryClick(cat)}
                  className="group bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex gap-4 cursor-pointer transition-all hover:shadow-md relative overflow-hidden"
                >
                  {/* CHOTI IMAGE BOX */}
                  <div className="w-24 h-24 flex-shrink-0 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-center overflow-hidden p-2">
                    {cat.image_url ? (
                      <img 
                        src={cat.image_url} 
                        alt={cat.name} 
                        className="w-full h-full object-contain group-hover:scale-110 transition-transform"
                      />
                    ) : (
                      <Package size={24} className="text-gray-300" />
                    )}
                  </div>

                  {/* DESCRIBE SECTION (FULL FILL CONTENT) */}
                  <div className="flex flex-col justify-between flex-grow">
                    <div>
                      <h3 className="font-bold text-gray-900 text-sm group-hover:text-[#00684a] transition-colors leading-tight mb-1">
                        {cat.name}
                      </h3>
                      <p className="text-[11px] text-gray-500 line-clamp-2 leading-relaxed mb-2">
                        Premium quality {cat.name} sourced from verified TradyFrog suppliers. Best for industrial and bulk needs.
                      </p>
                    </div>

                    {/* MINI SUB-LIST FOR FILLING SPACE */}
                    <div className="flex flex-wrap gap-x-2 gap-y-1">
                      {cat.subCategories?.slice(0, 3).map(sub => (
                        <span key={sub.id} className="text-[10px] text-[#00684a] font-medium bg-emerald-50 px-1.5 py-0.5 rounded">
                          {sub.name}
                        </span>
                      ))}
                      {cat.subCategories && cat.subCategories.length > 3 && (
                        <span className="text-[10px] text-gray-400">+{cat.subCategories.length - 3} more</span>
                      )}
                    </div>
                  </div>

                  <ArrowRight size={14} className="absolute bottom-3 right-3 text-gray-300 group-hover:text-[#00684a] transition-colors" />
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        )}
      </main>
    </article>
  );
};

export default AllCategoriesPage;